import { EXTERNAL_API_CONFIG } from "../config/externalApi.config";
interface StoredProcedureParams {
  [key: string]: any;
}
export class ExternalApiService {
  private static instance: ExternalApiService;
  private token: string | null = null;
  private tokenExpiry: number | null = null;

  private constructor() {
    this.loadTokenFromStorage();
  }

  public static getInstance(): ExternalApiService {
    if (!ExternalApiService.instance) {
      ExternalApiService.instance = new ExternalApiService();
    }
    return ExternalApiService.instance;
  }

  private loadTokenFromStorage(): void {
    this.token = localStorage.getItem(EXTERNAL_API_CONFIG.TOKEN_KEY);
    const expiry = localStorage.getItem(EXTERNAL_API_CONFIG.TOKEN_EXPIRY_KEY);
    this.tokenExpiry = expiry ? parseInt(expiry, 10) : null;
  }

  private saveTokenToStorage(token: string, expiresIn?: number): void {
    this.token = token;
    localStorage.setItem(EXTERNAL_API_CONFIG.TOKEN_KEY, token);

    if (expiresIn) {
      const expiryTime = Date.now() + expiresIn * 1000;
      this.tokenExpiry = expiryTime;
      localStorage.setItem(EXTERNAL_API_CONFIG.TOKEN_EXPIRY_KEY, expiryTime.toString());
    }
  }

  private clearToken(): void {
    this.token = null;
    this.tokenExpiry = null;
    localStorage.removeItem(EXTERNAL_API_CONFIG.TOKEN_KEY);
    localStorage.removeItem(EXTERNAL_API_CONFIG.TOKEN_EXPIRY_KEY);
  }

  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) return true;
    return Date.now() >= this.tokenExpiry;
  }

  public async authenticate(): Promise<string> {
    try {
      const requestBody = {
        userName: EXTERNAL_API_CONFIG.USERNAME,
        password: EXTERNAL_API_CONFIG.PASSWORD,
        channel: "1",
      };

      const response = await fetch(EXTERNAL_API_CONFIG.AUTH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error(`Authentication failed: ${response.statusText} - ${errorText}`);
      }

      const data: any = await response.json();
      const token = data.Token || data.token || data.accessToken;

      if (!token) {
        console.error("❌ No token found in response:", data);
        throw new Error("No token in authentication response");
      }

      this.saveTokenToStorage(token, data.expiresIn);

      return token;
    } catch (error) {
      console.error("Authentication error:", error);
      throw error;
    }
  }

  public async getToken(): Promise<string> {
    if (this.token && !this.isTokenExpired()) {
      return this.token;
    }
    return await this.authenticate();
  }

  public async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const token = await this.getToken();

      const url = `${EXTERNAL_API_CONFIG.BASE_URL}${endpoint}`;

      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        headers: {
          ...options.headers,
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        this.clearToken();
        const newToken = await this.authenticate();

        const retryResponse = await fetch(url, {
          ...options,
          mode: 'cors',
          headers: {
            ...options.headers,
            "Authorization": `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          console.error("❌ Retry failed:", errorText);
          throw new Error(`Request failed: ${retryResponse.statusText} - ${errorText}`);
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Request failed:", errorText);
        throw new Error(`Request failed: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error("External API request error:", error);
      throw error;
    }
  }

  /**
   * Fetch a single customer by identification
   */
  public async getCustomerByIdentification<T>(identification: string, identificationType: string): Promise<T> {
    const params = new URLSearchParams({
      identification: identification,
      identificationType: identificationType
    });
    const endpoint = `${EXTERNAL_API_CONFIG.CUSTOMER_BY_IDENTIFICATION_ENDPOINT}?${params.toString()}`;
    return await this.request<T>(endpoint);
  }

  /**
   * Excecute StoreProcedure Demo.InsertarOperacionesBursatiles
   */
  public async excecuteSP<T>(requestBody: StoredProcedureParams = {}): Promise<T> {
    try {
      const Sql = "EXEC Demo.InsertarOperacionesBursatiles @p1,@p2,@p3,@p4,@p5,@p6,@p7,@p8,@p9,@p10,@p11,@p12";

      const Params = [
        requestBody.Id,
        requestBody.OrderType,
        requestBody.Status,
        requestBody.RentType,
        requestBody.ClientIdentification,
        requestBody.ClientIdentificationType,
        requestBody.Instrument,
        requestBody.Quantity,
        requestBody.Rate,
        requestBody.Value,
        requestBody.ConfirmNumber,
        requestBody.CreatedAt
      ];

      const endpoint = EXTERNAL_API_CONFIG.EXECUTE_SP_ENDPOINT;

      return await this.request<T>(endpoint, {
        method: "POST",
        body: JSON.stringify({ Sql, Params }),
      });

    } catch (error) {
      console.error("Authentication error:", error);
      throw error;
    }
  }
}

// Export a singleton instance
export const externalApiService = ExternalApiService.getInstance();
